import HeaderProjectNameComponent from "@/components/common-components/header-project-name/HeaderProjectNameComponent.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { ChevronLeft, HistoryIcon, PanelLeftClose, Users } from "lucide-react";
import { useCallback, useState } from "react";
import CommitHistoryComponent from "../../app-tabs/app-tabs-header/session-history/SessionHistoryComponent.tsx";
import "./ChatScreenHeaderComponent.scss";
import { AppType } from "@/models/project.model.ts";
import CreditsPopoverComponent from "@/components/common-components/credits-popover/CreditsPopoverComponent";
import { useNavigate } from "react-router-dom";
import { REQUIREMENT_GATHERING_ROUTE } from "@/constants/RoutesConfig.tsx";

interface ChatScreenHeaderComponentProps {
    onExitChat: () => void;
    projectId: string;
    appType: AppType;
    onCollapseChat: () => void;
}

const ChatScreenHeaderComponent = (props: ChatScreenHeaderComponentProps) => {
    const { onExitChat, projectId, appType, onCollapseChat } = props;
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState<boolean>(false);

    const handleOpenHistoryDrawer = useCallback(() => {
        setIsHistoryDrawerOpen(true);
    }, []);

    const handleOpenSpecializedAgents = useCallback(() => {
        navigate(REQUIREMENT_GATHERING_ROUTE + '/' + projectId);
    }, [navigate, projectId]);

    return (
        <>
            <div className="chat-screen-header">
                <div className="chat-screen-header-left">
                    <Button
                        onClick={onExitChat}
                        variant="outline"
                        size={isMobile ? "icon" : "sm"}
                        mixpanelEvent={{ name: "exit_chat_clicked" }}
                    >
                        <ChevronLeft className={isMobile ? "h-5 w-5" : "h-4 w-4"} />
                    </Button>
                    <HeaderProjectNameComponent />
                </div>
                <div className="chat-screen-header-right">
                    {isMobile && (
                        <CreditsPopoverComponent />
                    )}

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleOpenHistoryDrawer}
                                mixpanelEvent={{ name: "git_history_button_clicked" }}
                                className="flex items-center gap-2"
                            >
                                <HistoryIcon size={14} />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>History</p>
                        </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleOpenSpecializedAgents}
                                mixpanelEvent={{ name: "specialized_agents_button_clicked" }}
                                className="flex items-center gap-2"
                            >
                                <Users size={14} />
                                {!isMobile && <span className="text-xs">Agents</span>}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Specialized Agents (Analyst/PM)</p>
                        </TooltipContent>
                    </Tooltip>

                    {!isMobile && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onCollapseChat}
                                    mixpanelEvent={{ name: "collapse_chat_clicked" }}
                                    className="flex items-center gap-2"
                                >
                                    <PanelLeftClose size={14} />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Collapse Chat</p>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            <CommitHistoryComponent
                isHistoryDrawerOpen={isHistoryDrawerOpen}
                projectId={projectId}
                onClose={() => setIsHistoryDrawerOpen(false)}
                appType={appType}
            />
        </>
    );
};

export default ChatScreenHeaderComponent;
